import {
	type CreateLightningReportRequest,
	type CreateLightningReportScheduleRequest,
	useCreateLightningReport,
	useCreateLightningReportSchedule,
} from '../../../../data/reports/lightning.ts'
import { dayjs } from '../../../../dayjs.ts'
import { isNonEmpty } from '../schema.ts'
import {
	type LightningReportForm,
	type OneOffTiming,
	oneOffTimingToTimeRange,
	type RepeatedTiming,
	transformSignalStrengthRanges,
	useIsFutureOneOffReport,
} from './schema.tsx'

const uppercase = <T extends string>(value: T) => value.toUpperCase() as Uppercase<T>

// Form schedule dates are stored as UTC midnight of the chosen calendar date (timezone-independent).
// The API expects midnight in the user's timezone, expressed as UTC ISO — shift accordingly.
const toScheduleAtApiIso = (storedUtcMidnightIso: string, timezone: string): string => {
	const dateStr = dayjs.utc(storedUtcMidnightIso).format('YYYY-MM-DD')
	return dayjs.tz(`${dateStr}T00:00:00`, timezone).toISOString()
}

export const transformRepeatedTimingToScheduleRequest = (
	data: LightningReportForm & { timing: RepeatedTiming },
): CreateLightningReportScheduleRequest => {
	const { assetVrns } = data
	if (!isNonEmpty(assetVrns)) throw new Error('A report needs an asset')

	return {
		name: data.name,
		assetVrns,
		scheduleType: data.timing.type,
		frequency: data.timing.frequency,
		dayOfWeek: data.timing.dayOfWeek,
		radiusKm: data.radiusKm,
		geometry: data.geometry,
		customPoint: data.customPoint,
		startScheduleAt: toScheduleAtApiIso(data.timing.startScheduleAt, data.preferences.timezone),
		stopScheduleAt: data.timing.stopScheduleAt ? toScheduleAtApiIso(data.timing.stopScheduleAt, data.preferences.timezone) : undefined,
		preferences: data.preferences,
		errorEllipseConfidence: data.errorEllipseConfidence,
		includeStrikeDamagePotential: data.includeStrikeDamagePotential,
		includeCloudToCloud: data.includeCloudToCloud,
		signalStrengthRanges: transformSignalStrengthRanges(data.signalStrengthRange),
		dataset: data.dataset,
	}
}

export const transformOneOffTimingToReportRequest = (
	data: LightningReportForm & { timing: OneOffTiming },
): CreateLightningReportRequest => {
	const { assetVrns } = data
	if (!isNonEmpty(assetVrns)) throw new Error('A report needs an asset')

	return {
		name: data.name,
		assetVrns,
		timeRange: oneOffTimingToTimeRange(data.timing),
		faultTime:
			data.timing.time.type === 'fault-time'
				? {
						time: data.timing.time.faultTime,
						buffer: { ...data.timing.time.buffer, unit: uppercase(data.timing.time.buffer.unit) },
					}
				: undefined,
		radiusKm: data.radiusKm,
		geometry: data.geometry,
		customPoint: data.customPoint,
		preferences: data.preferences,
		errorEllipseConfidence: data.errorEllipseConfidence,
		includeStrikeDamagePotential: data.includeStrikeDamagePotential,
		includeCloudToCloud: data.includeCloudToCloud,
		signalStrengthRanges: transformSignalStrengthRanges(data.signalStrengthRange),
		dataset: data.dataset,
	}
}

export const transformOneOffTimingToScheduleRequest = (
	data: LightningReportForm & { timing: OneOffTiming },
): CreateLightningReportScheduleRequest => ({
	...transformOneOffTimingToReportRequest(data),
	scheduleType: 'ONE_OFF',
})

// A one-off report is created directly when its data is available, and as a schedule otherwise
export const useLightningSubmit = () => {
	const createLightningReport = useCreateLightningReport()
	const createLightningReportSchedule = useCreateLightningReportSchedule()
	const isFutureOneOffReport = useIsFutureOneOffReport()

	return (data: LightningReportForm): Promise<unknown> => {
		switch (data.timing.type) {
			case 'REPEATED':
				return createLightningReportSchedule.mutateAsync(transformRepeatedTimingToScheduleRequest({ ...data, timing: data.timing }))
			case 'ONE_OFF': {
				if (isFutureOneOffReport(data)) {
					return createLightningReportSchedule.mutateAsync(transformOneOffTimingToScheduleRequest({ ...data, timing: data.timing }))
				}

				return createLightningReport.mutateAsync(transformOneOffTimingToReportRequest({ ...data, timing: data.timing }))
			}
			default:
				data.timing satisfies never
				throw new Error('Unrecognized timing')
		}
	}
}
